import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { pd, isNonWorking } from './businessDays';
import { PHASE_COLORS, mvpColor, FLAT_COLOR } from './timelineColors';
import './TimelineGantt.css';

/**
 * The Gantt for Timeline Finalization.
 *
 * CLONED from `ai_readiness_template/views/roadmap-execution/finalization.ejs` —
 * `segsOf`, `drawGantt`, `drawGanttInto`. The screen was ported in August WITHOUT it, which is
 * why this file is new rather than edited: nothing was removed, the chart was simply never
 * built. Its geometry, shading and today-line are the source's, cited inline.
 *
 * ── WHAT A ROW CONTAINS, AND WHY THE METHODOLOGY DECIDES ───────────────────────────────
 *
 *   Waterfall   one bar per phase, in the five phase colours
 *   Agile       one bar per sprint, coloured by its MVP wave
 *   None        ONE bar spanning the initiative's own window — it has no phases to draw, and
 *               an empty lane reads as missing data rather than as a deliberate choice
 *
 * ── ONE DELIBERATE DEVIATION FROM THE SOURCE, AND WHY ──────────────────────────────────
 *
 * The source's bar palette is PASTEL (`#90caf9`, `#ce93d8`, `#a5d6a7`, `#ffcc80`, `#ef9a9a`).
 * This screen already draws its phase DOTS in the stronger set (`#42a5f5` …), and the owner's
 * standing instruction is that this application's UI design is not to be changed. Matching the
 * source's pastels here would leave the dot and the bar for the same phase in two different
 * colours on one screen, so the bars use the colours this screen already has. Logged rather
 * than done silently.
 */

/* Geometry — the source's `drawGanttInto`, verbatim. */
const HEADER_H = 30;
const ROW_H = 26;
const BAR_H = 15;
const SIDE_W = 170;
const DAY = 86400000;

export interface GanttOpp {
  opportunity_id: string;
  methodology: string | null;
  planned_start_date: string | null;
  planned_end_date: string | null;
  phases: { name: string; start_date: string; end_date: string }[];
  agile?: { mvps?: { sprints?: { start: string | null; end: string | null }[] }[] } | null;
}

interface Seg {
  start: string;
  end: string;
  color: string;
}

const isFlat = (o: GanttOpp) => {
  const m = (o.methodology ?? '').trim();
  return m === '' || m === 'None';
};

/** The source's `segsOf`. */
function segsOf(o: GanttOpp): Seg[] {
  if (isFlat(o)) {
    return o.planned_start_date && o.planned_end_date
      ? [{ start: o.planned_start_date, end: o.planned_end_date, color: FLAT_COLOR }]
      : [];
  }
  if (o.methodology === 'Agile' && o.agile?.mvps?.length) {
    const segs: Seg[] = [];
    o.agile.mvps.forEach((m, mi) =>
      (m.sprints ?? []).forEach((s) => {
        if (s.start && s.end) segs.push({ start: s.start, end: s.end, color: mvpColor(mi) });
      }),
    );
    return segs;
  }
  return (o.phases ?? [])
    .filter((p) => p.start_date && p.end_date)
    .map((p) => ({
      start: p.start_date,
      end: p.end_date,
      color: PHASE_COLORS[p.name] ?? '#bbb',
    }));
}

export function TimelineGantt({
  opportunities,
  single = false,
  zoom,
  onZoom,
}: {
  opportunities: GanttOpp[];
  /** A single-opportunity chart labels its side column "Timeline" — the source's `single`. */
  single?: boolean;
  zoom: number | 'fit';
  onZoom: (z: number | 'fit') => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [rows, setRows] = useState<{ id: string; tag: string }[]>([]);
  const [avail, setAvail] = useState(700);

  // 'fit' needs the container's real width, so it is measured rather than assumed — a typed
  // constant here would be another element's size and would break on the next layout change.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const read = () => setAvail(el.clientWidth || 700);
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const withP = opportunities.filter((o) => segsOf(o).length);
    if (!withP.length) {
      canvas.width = 1;
      canvas.height = 1;
      setRows([]);
      return;
    }

    const dates: Date[] = [];
    withP.forEach((o) =>
      segsOf(o).forEach((sg) => {
        const s = pd(sg.start);
        const e = pd(sg.end);
        if (s) dates.push(s);
        if (e) dates.push(e);
      }),
    );

    // A little air either side, then snapped to the start of the month — the source's rule.
    let minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));
    minDate.setDate(minDate.getDate() - 3);
    maxDate.setDate(maxDate.getDate() + 6);
    minDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);

    const totalDays = Math.ceil((maxDate.getTime() - minDate.getTime()) / DAY);
    const ppd =
      zoom === 'fit' ? Math.max(0.25, (avail - 2) / Math.max(1, totalDays)) : Number(zoom) || 6;

    const W = Math.max(totalDays * ppd, 360);
    const H = HEADER_H + withP.length * ROW_H;
    // Draw at device resolution so bars and 10px labels are not blurred on a retina screen.
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, W, H);

    // Weekends and holidays, shaded the full height.
    for (let d = 0; d < totalDays; d++) {
      const dt = new Date(minDate);
      dt.setDate(dt.getDate() + d);
      if (isNonWorking(dt)) {
        ctx.fillStyle = 'rgba(140,140,140,0.08)';
        ctx.fillRect(d * ppd, HEADER_H, ppd, H - HEADER_H);
      }
    }

    // Month band.
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, W, HEADER_H);
    ctx.fillStyle = '#333';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    let cm = minDate.getMonth();
    let ms = 0;
    for (let d = 0; d <= totalDays; d++) {
      const dt = new Date(minDate);
      dt.setDate(dt.getDate() + d);
      if (dt.getMonth() !== cm || d === totalDays) {
        const x = ms * ppd + ((d - ms) * ppd) / 2;
        const label = new Date(minDate.getTime() + ms * DAY).toLocaleDateString('en-US', {
          month: 'short',
          year: '2-digit',
        });
        ctx.fillText(label, x, 19);
        cm = dt.getMonth();
        ms = d;
      }
    }

    withP.forEach((o, i) => {
      const y = HEADER_H + i * ROW_H;
      const barY = y + (ROW_H - BAR_H) / 2;
      segsOf(o).forEach((sg) => {
        const s = pd(sg.start);
        const e = pd(sg.end);
        if (!s || !e) return;
        const sOff = (s.getTime() - minDate.getTime()) / DAY;
        const eOff = (e.getTime() - minDate.getTime()) / DAY;
        const px = sOff * ppd;
        const pw = Math.max((eOff - sOff + 1) * ppd, 2);
        ctx.fillStyle = sg.color;
        ctx.fillRect(px, barY, pw, BAR_H);
        // Non-working days are greyed INSIDE the bar too, so a span reads as working time.
        for (let dOff = Math.floor(sOff); dOff < Math.ceil(eOff) + 1; dOff++) {
          const dt = new Date(minDate);
          dt.setDate(dt.getDate() + dOff);
          if (isNonWorking(dt)) {
            const cx = dOff * ppd;
            const l = Math.max(cx, px);
            const r = Math.min(cx + ppd, px + pw);
            if (r > l) {
              ctx.fillStyle = '#d9d9d9';
              ctx.fillRect(l, barY, r - l, BAR_H);
            }
          }
        }
      });
      ctx.strokeStyle = '#eee';
      ctx.beginPath();
      ctx.moveTo(0, y + ROW_H);
      ctx.lineTo(W, y + ROW_H);
      ctx.stroke();
    });

    // Today.
    const tOff = (Date.now() - minDate.getTime()) / DAY;
    if (tOff >= 0 && tOff <= totalDays) {
      const tx = tOff * ppd;
      ctx.strokeStyle = '#e74c3c';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(tx, HEADER_H);
      ctx.lineTo(tx, H);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.lineWidth = 1;
    }

    setRows(
      withP.map((o) => ({
        id: o.opportunity_id,
        tag: o.methodology === 'Agile' ? 'Agile' : isFlat(o) ? 'None' : '',
      })),
    );
  }, [opportunities, zoom, avail]);

  if (!opportunities.some((o) => segsOf(o).length)) {
    return (
      <div className="tg-empty">
        No dates to chart yet — set a start date, or use Auto-fill, and the timeline appears here.
      </div>
    );
  }

  return (
    <div className="tg">
      <div className="tg-zoom">
        <span className="tg-zoom-lbl">Zoom</span>
        <button
          type="button"
          title="Zoom out"
          onClick={() => onZoom(zoom === 'fit' ? 4 : Math.max(1, (zoom as number) - 2))}
        >
          −
        </button>
        <button
          type="button"
          title="Zoom in"
          onClick={() => onZoom(zoom === 'fit' ? 8 : Math.min(40, (zoom as number) + 2))}
        >
          +
        </button>
        {/* 6 px per day is the source's default, so that reads as 100%. */}
        <span className="tg-zoom-pct">
          {zoom === 'fit' ? 'Fit' : `${Math.round(((zoom as number) / 6) * 100)}%`}
        </span>
        <button
          type="button"
          className={zoom === 'fit' ? 'on' : ''}
          title="Fit the whole timeline on one screen"
          onClick={() => onZoom('fit')}
        >
          Fit
        </button>
      </div>

      <div className="tg-frame">
        <div className="tg-side" style={{ width: SIDE_W, minWidth: SIDE_W }}>
          <div className="tg-side-hd" style={{ height: HEADER_H }}>
            {single ? 'Timeline' : 'Opportunity'}
          </div>
          {rows.map((r) => (
            <div className="tg-side-row" style={{ height: ROW_H }} key={r.id} title={r.id}>
              <strong>{r.id}</strong>
              {r.tag ? <span className={`tg-tag ${r.tag.toLowerCase()}`}> · {r.tag}</span> : null}
            </div>
          ))}
        </div>
        <div className="tg-scroll" ref={scrollRef}>
          <canvas ref={canvasRef} />
        </div>
      </div>
    </div>
  );
}
